import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Handshake, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { subCommitmentsApi, projectsApi, subcontractorsApi } from '../api';
import { fmtDate, centsToDollarString } from '../constants';
import { COMMITMENT_TYPE_OPTIONS, COMMITMENT_STATUS_OPTIONS, COMMITMENT_SORT_OPTIONS, COMMITMENT_STATUS_STYLES, commitmentTypeLabel, commitmentStatusLabel } from '../subConstants';
import { CommitmentFormDialog } from './CommitmentFormDialog';

const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';

export const CommitmentsView = ({ lockedProjectId, lockedSubcontractorId, disableAdd = false, refreshKey = 0, onChanged }) => {
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [subs, setSubs] = useState([]);
  const [filters, setFilters] = useState({ search: '', project_id: '', subcontractor_id: '', commitment_type: '', status: '', start_date: '', end_date: '', sort: 'date_newest' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!lockedProjectId) projectsApi.list({ sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
    if (!lockedSubcontractorId) subcontractorsApi.list({}).then((r) => setSubs(r.data)).catch(() => {});
  }, [lockedProjectId, lockedSubcontractorId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { sort: filters.sort };
      if (lockedProjectId) p.project_id = lockedProjectId;
      else if (filters.project_id) p.project_id = filters.project_id;
      if (lockedSubcontractorId) p.subcontractor_id = lockedSubcontractorId;
      else if (filters.subcontractor_id) p.subcontractor_id = filters.subcontractor_id;
      if (filters.search.trim()) p.search = filters.search.trim();
      if (filters.commitment_type) p.commitment_type = filters.commitment_type;
      if (filters.status) p.status = filters.status;
      if (filters.start_date) p.start_date = filters.start_date;
      if (filters.end_date) p.end_date = filters.end_date;
      const res = await subCommitmentsApi.list(p);
      setCommitments(res.data);
    } catch {
      toast.error('Could not load commitments');
    } finally { setLoading(false); }
  }, [filters, lockedProjectId, lockedSubcontractorId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setF = (k, v) => setFilters((x) => ({ ...x, [k]: v }));

  return (
    <div data-testid="commitments-view">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Input value={filters.search} onChange={(e) => setF('search', e.target.value)} placeholder="Search commitments…" className={`${inputCls} w-full sm:w-56`} data-testid="cm-filter-search" />
        {!lockedProjectId && (
          <select value={filters.project_id} onChange={(e) => setF('project_id', e.target.value)} className={selectCls} data-testid="cm-filter-project">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {!lockedSubcontractorId && (
          <select value={filters.subcontractor_id} onChange={(e) => setF('subcontractor_id', e.target.value)} className={selectCls} data-testid="cm-filter-sub">
            <option value="">All subcontractors</option>
            {subs.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
          </select>
        )}
        <select value={filters.commitment_type} onChange={(e) => setF('commitment_type', e.target.value)} className={selectCls} data-testid="cm-filter-type">
          <option value="">All types</option>
          {COMMITMENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setF('status', e.target.value)} className={selectCls} data-testid="cm-filter-status">
          <option value="">All statuses</option>
          {COMMITMENT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Input type="date" value={filters.start_date} onChange={(e) => setF('start_date', e.target.value)} className={`${inputCls} w-auto`} data-testid="cm-filter-start" />
        <Input type="date" value={filters.end_date} onChange={(e) => setF('end_date', e.target.value)} className={`${inputCls} w-auto`} data-testid="cm-filter-end" />
        <select value={filters.sort} onChange={(e) => setF('sort', e.target.value)} className={selectCls} data-testid="cm-filter-sort">
          {COMMITMENT_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {!disableAdd && (
          <Button onClick={() => { setActive(null); setDialogOpen(true); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold ml-auto" data-testid="cm-add-btn">
            <Plus size={16} /> New Commitment
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">
          <Loader2 size={26} className="mx-auto mb-3 animate-spin text-[#D4AF37]" /> Loading commitments…
        </div>
      ) : commitments.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="cm-empty">
          <Handshake size={32} className="mx-auto mb-3 text-gray-700" />
          <p className="text-gray-500">No commitments yet. Draft commitments do not count as project cost until approved.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="cm-list">
          {commitments.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActive(c); setDialogOpen(true); }}
              className={`w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-[#111111] hover:bg-[#1A1A1A] border border-gray-800 rounded-lg px-4 py-3 text-left transition-colors ${c.status === 'cancelled' ? 'opacity-60' : ''}`}
              data-testid={`cm-row-${c.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium">{c.title}</span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${COMMITMENT_STATUS_STYLES[c.status]}`}>{commitmentStatusLabel(c.status)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.commitment_type === 'extra_work' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30' : 'bg-gray-800 text-gray-400 border-gray-700'}`}>
                    {commitmentTypeLabel(c.commitment_type)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {fmtDate(c.commitment_date)} · {c.project?.name || 'Unknown Project'} · {c.subcontractor?.display_name}
                  {c.parent_commitment ? ` · Extra to: ${c.parent_commitment.title}` : ''}
                  {c.work_category ? ` · ${c.work_category}` : ''}
                </div>
              </div>
              <div className="text-sm font-semibold text-gray-100 shrink-0">{centsToDollarString(c.amount_cents)}</div>
            </button>
          ))}
        </div>
      )}

      <CommitmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        commitment={active}
        lockedProjectId={lockedProjectId}
        lockedSubcontractorId={lockedSubcontractorId}
        onSaved={() => { load(); onChanged?.(); }}
      />
    </div>
  );
};
